import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from 'react';
import type { GwsBackupFilter, GwsGroup, GwsOrgUnit } from '@/app/(dashboard)/saas-inventory/google-workspace/backupSettings/backupSettingsTypes';
import type { GWConnectorNode, GWNode } from '@/app/(dashboard)/saas-inventory/google-workspace/googleWorkspaceTypes';
import type { GWSnapshot } from '@/app/(dashboard)/saas-inventory/google-workspace/snapshotTypes';
import { getBackupSettings, groupsMockData, orgUnitsMockData, setBackupSettings } from '@/app/api/mock/projects/[projectId]/saas-inventory/google-workspace/backupSettingsData';
import { googleWorkspaceMockData } from '@/app/api/mock/projects/[projectId]/saas-inventory/google-workspace/mockData';
import { snapshotRemovals, snapshotsMockData } from '@/app/api/mock/projects/[projectId]/saas-inventory/google-workspace/snapshots';
function filterTree<T extends {
  id: string;
  children?: T[];
}>(nodes: T[], removeIds: Set<string>): T[] {
  return nodes.filter(n => !removeIds.has(n.id)).map(n => ({
    ...n,
    children: n.children ? filterTree(n.children, removeIds) : undefined
  }));
}
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = hash * 31 + str.charCodeAt(i) | 0;
  }
  return Math.abs(hash);
}
function capDates(node: any, snapshotDate: string, sizeFactor: number): any {
  const snapTime = new Date(snapshotDate).getTime();
  const result = {
    ...node
  };
  if (result.modified) {
    const modTime = new Date(result.modified).getTime();
    if (modTime > snapTime) {
      const offsetMs = (1 + simpleHash(result.id) % 24) * 3_600_000;
      result.modified = new Date(snapTime - offsetMs).toISOString();
    }
  }
  if (result.date) {
    const dateTime = new Date(result.date).getTime();
    if (dateTime > snapTime) {
      const offsetMs = (1 + simpleHash(result.id) % 48) * 3_600_000;
      result.date = new Date(snapTime - offsetMs).toISOString();
    }
  }
  if (result.lastBackup) {
    result.lastBackup = snapshotDate;
  }
  if (result.size && sizeFactor < 1) {
    result.size = Math.round(result.size * sizeFactor);
  }
  if (result.children) {
    result.children = result.children.map((child: any) => capDates(child, snapshotDate, sizeFactor));
  }
  return result;
}
function buildConnectorTree(connectorId: string, snapshotId: string): GWConnectorNode {
  const base = googleWorkspaceMockData.find(c => c.id === connectorId) ?? googleWorkspaceMockData[0];
  const latestId = snapshotsMockData[0]?.id;
  if (snapshotId === latestId) {
    return base;
  }
  const snapshot = snapshotsMockData.find(s => s.id === snapshotId);
  if (!snapshot) {
    return base;
  }
  const sizeFactor = snapshot.sizeBytes / snapshotsMockData[0].sizeBytes;
  const removeIds = new Set(snapshotRemovals[snapshotId] ?? []);
  const filtered: GWNode[] = filterTree<GWNode>([base], removeIds).map(n => capDates(n, snapshot.date, sizeFactor));
  const result = filtered[0] ?? base;
  return result.type === 'connector' ? result : base;
}

// Real React hook (unlike its `use*` siblings, which are plain SWR-shaped
// functions). Single-consumer: state is component-local — a second reader of
// the same resourceId would not see this one's saves. Move to a shared store
// before adding a second consumer.
function useBackupSettingsHook(resourceId) {
  const $ = _c(9);
  let t0;
  if ($[0] !== resourceId) {
    t0 = () => getBackupSettings(resourceId);
    $[0] = resourceId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [filter, setFilter] = useState(t0);
  let t1;
  if ($[2] !== resourceId) {
    t1 = async next => {
      setBackupSettings(resourceId, next);
      setFilter(next);
    };
    $[2] = resourceId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const update = t1;
  let t2;
  if ($[4] !== filter) {
    t2 = {
      filter
    };
    $[4] = filter;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2 || $[7] !== update) {
    t3 = {
      data: t2,
      isLoading: false,
      update
    };
    $[6] = t2;
    $[7] = update;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export const saasInventory = () => ({
  googleWorkspace: {
    useSnapshots: (): {
      data: GWSnapshot[];
      isLoading: false;
    } => ({
      data: snapshotsMockData,
      isLoading: false
    }),
    useConnectorTree: (connectorId: string, snapshotId: string | null): {
      data: GWConnectorNode | undefined;
      isLoading: false;
    } => ({
      data: snapshotId ? buildConnectorTree(connectorId, snapshotId) : undefined,
      isLoading: false
    }),
    // Mock returns the same fixture for any connector; the `void` discard
    // keeps the param in the signature without tripping no-unused-vars (the
    // project's rule rejects `_` prefix).
    useOrgUnits: (connectorId: string): {
      data: GwsOrgUnit[];
      isLoading: false;
    } => {
      void connectorId;
      return {
        data: orgUnitsMockData,
        isLoading: false
      };
    },
    useGroups: (connectorId: string): {
      data: GwsGroup[];
      isLoading: false;
    } => {
      void connectorId;
      return {
        data: groupsMockData,
        isLoading: false
      };
    },
    useBackupSettings: useBackupSettingsHook
  }
});